import { Box, Button, FormControl, FormLabel, HStack, Text, useColorModeValue, useToast } from "@chakra-ui/react";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import { useEffect, useState } from "react";
import FormField from "../../../components/fields/FormField";
import FormModal from "../../../components/FormModal";
import { translate } from "../../../i18n";
import { typography } from 'theme/tokens';
import {
    CourtTemporaryBlockadeDto,
    CourtTemporaryBlockadeReason,
} from "../../../models/court/dto/court-temporary-blockade.dto";
import { useCancelAdminBooking } from "../hooks/useCancelAdminBooking";
import { useDeleteCourtBlockade } from "../hooks/useDeleteCourtBlockade";
import { useGetAdminBookingCheckoutUrl } from "../hooks/useGetAdminBookingCheckoutUrl";
import BlockadeReasonPicker from "./BlockadeReasonPicker";
import { useIsOrgManager } from "../../auth/roles";

type Props = {
    isModalVisible: boolean;
    setIsModalVisible: (newValue: boolean) => void;
    onBlockadeDeleted: (blockadeId: string) => void;
    selectedBlockade?: CourtTemporaryBlockadeDto;
    /**
     * Required to route PENDING_PAYMENT blockades through the admin-booking
     * cancel endpoint, which expires the Stripe Checkout session before
     * deleting the row. Without it, an operator deleting a pending hold
     * could still see a Game created moments later if the player pays.
     */
    organizationId?: string;
};

export default function SelectedBlockadeModal({
    selectedBlockade,
    isModalVisible,
    setIsModalVisible,
    onBlockadeDeleted,
    organizationId,
}: Props) {
    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();
    // Of the three actions this modal can offer, two are
    // hasOrganizationAdminAccess on the API — `DELETE
    // payments/admin-bookings/:blockadeId` and `GET
    // payments/admin-bookings/:blockadeId/checkout-url` — and one is
    // staff-level (admin OR coach): `DELETE court-temporary-blockades/:id`.
    // Both calendars that open this modal are coach-reachable and route every
    // event click through `onEventSelected` with no role check, so a coach
    // reaches all three; the admin-only pair stays hidden for them (the
    // checkout-url failure used to be swallowed into a generic "Réessaie dans
    // un instant" toast rather than reading as a permission error), while a
    // plain blockade's delete is offered to anyone who could have created it.
    const isOrgManager = useIsOrgManager();
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const {
        mutateAsync: fetchCheckoutUrl,
        isLoading: isFetchingCheckoutUrl,
    } = useGetAdminBookingCheckoutUrl();

    // Reset the inline confirmation whenever the modal closes or the
    // selected blockade changes, so a stale confirm doesn't surprise the
    // operator on next open.
    useEffect(() => {
        if (!isModalVisible) {
            setShowCancelConfirm(false);
        }
    }, [isModalVisible, selectedBlockade?.id]);

    const { mutateAsync: deleteBlockade, isLoading: isDeleting } = useDeleteCourtBlockade((blockadeId) => {
        onBlockadeDeleted(blockadeId);
        setIsModalVisible(false);
    });

    const isPendingPayment =
        selectedBlockade?.reason === CourtTemporaryBlockadeReason.PENDING_PAYMENT;

    const { mutateAsync: cancelAdminBooking, isLoading: isCanceling } =
        useCancelAdminBooking(() => {
            if (selectedBlockade) {
                onBlockadeDeleted(selectedBlockade.id);
            }
            setIsModalVisible(false);
        });

    if (!selectedBlockade) return null;

    const canCancelAdmin = isPendingPayment && Boolean(organizationId);

    // Race guard: a PENDING_PAYMENT blockade with no organizationId yet means
    // useGetCurrentOrganization is still loading. Falling back to the legacy
    // delete path here would skip the Stripe-session expire and leave a
    // payable orphan. Hide the destructive action entirely so the operator
    // waits for the org context to load.
    const isOrgLoadingForPendingPayment =
        isPendingPayment && !organizationId;

    const destructiveLabel = canCancelAdmin
        ? 'Annuler la réservation'
        : translate('delete');

    const destructiveAction = canCancelAdmin
        ? () => setShowCancelConfirm(true)
        : () => deleteBlockade(selectedBlockade.id);

    const confirmCancelAdminBooking = () =>
        cancelAdminBooking({
            blockadeId: selectedBlockade.id,
            organizationId: organizationId!,
        });

    const handleCopyCheckoutUrl = async () => {
        try {
            const result = await fetchCheckoutUrl({
                blockadeId: selectedBlockade.id,
                organizationId: organizationId!,
            });
            // Stripe wipes session.url once the session is no longer
            // payable. Surface the specific reason so the operator knows
            // whether to send a refund (paid) or recreate (expired).
            if (!result.url) {
                toast({
                    title:
                        result.status === 'complete'
                            ? 'Le joueur a déjà payé'
                            : 'Le lien a expiré',
                    description:
                        result.status === 'complete'
                            ? 'La réservation est confirmée — aucun lien à partager.'
                            : 'Annule cette réservation et recrée-en une nouvelle pour générer un nouveau lien.',
                    status: 'info',
                    duration: 5000,
                    position: 'top-right',
                    isClosable: true,
                });
                return;
            }
            await navigator.clipboard.writeText(result.url);
            toast({
                title: 'Lien copié',
                description: 'Tu peux le partager en WhatsApp / SMS.',
                status: 'success',
                duration: 3000,
                position: 'top-right',
            });
        } catch {
            // Clipboard API rejection or network error — fall back to a
            // generic message rather than the cryptic API error.
            toast({
                title: 'Impossible de récupérer le lien',
                description: 'Réessaie dans un instant.',
                status: 'error',
                duration: 4000,
                position: 'top-right',
            });
        }
    };

    // A coach may delete an ordinary blockade but not cancel a pending
    // admin booking (Stripe session expiry is admin-only), so for them the
    // PENDING_PAYMENT shape offers nothing.
    const isPendingPaymentForNonManager = isPendingPayment && !isOrgManager;

    // While the inline confirm banner is visible, suppress the original
    // destructive button to avoid two destructive affordances on screen.
    const shouldHideDestructive =
        isPendingPaymentForNonManager || isOrgLoadingForPendingPayment || showCancelConfirm;

    return (
        <FormModal
            isOpen={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            title={translate('calendar.create_blockade_or_court_availability_modal.blocked_court')}
            size="3xl"
            submitLabel={translate('close')}
            onSubmit={() => setIsModalVisible(false)}
            destructiveAction={
                shouldHideDestructive
                    ? undefined
                    : {
                          label: destructiveLabel,
                          onClick: destructiveAction,
                          isLoading: isDeleting || isCanceling,
                      }
            }
        >
            <FormControl>
                {isOrgManager && canCancelAdmin && (
                    <Box mb={3} p={3} borderWidth={1} borderRadius="md" bg="orange.50">
                        <Text fontSize="sm" color="orange.800" mb={2}>
                            Réservation en attente de paiement. Annuler expirera le lien
                            Stripe pour que le joueur ne puisse plus payer.
                        </Text>
                        {/* Recovery path for operators who dismissed the create-success
                            modal without copying the link. Fetches on-demand from Stripe
                            so the URL reflects the live session state (open vs paid vs
                            expired) rather than a stale cache. */}
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="orange"
                            onClick={handleCopyCheckoutUrl}
                            isLoading={isFetchingCheckoutUrl}
                        >
                            Copier le lien de paiement
                        </Button>
                    </Box>
                )}
                {isOrgLoadingForPendingPayment && (
                    <Box mb={3} p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                        <Text fontSize="sm" color="gray.700">
                            Chargement du contexte organisation… L'annulation sera
                            disponible dans un instant.
                        </Text>
                    </Box>
                )}
                {showCancelConfirm && canCancelAdmin && (
                    <Box mb={3} p={3} borderWidth={1} borderRadius="md" bg="red.50">
                        <Text fontSize="sm" color="red.800" mb={3}>
                            Annuler la réservation ? Le lien de paiement Stripe sera
                            expiré et le créneau libéré.
                        </Text>
                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                colorScheme="red"
                                onClick={confirmCancelAdminBooking}
                                isLoading={isCanceling}
                            >
                                Confirmer l'annulation
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowCancelConfirm(false)}
                                isDisabled={isCanceling}
                            >
                                Retour
                            </Button>
                        </HStack>
                    </Box>
                )}
                <FormField
                    label={translate('courts.blockade.comment')}
                    value={selectedBlockade.description}
                    isDisabled
                    onChange={() => {}}
                />
                <FormField
                    label={translate('event.start_date')}
                    value={format(new Date(selectedBlockade.startTime), "dd MMMM yyyy HH:mm", { locale: fr })}
                    isDisabled
                    onChange={() => {}}
                />
                <FormField
                    label={translate('event.end_date')}
                    value={format(new Date(selectedBlockade.endTime), "dd MMMM yyyy HH:mm", { locale: fr })}
                    isDisabled
                    onChange={() => {}}
                />
                <FormControl>
                    <FormLabel {...typography.formLabel} color={labelColor} mb="6px">
                        {translate('calendar.create_blockade_or_court_availability_modal.blockade')}
                    </FormLabel>
                    <BlockadeReasonPicker
                        selectedReason={selectedBlockade.reason}
                        setSelectedReason={() => {}}
                    />
                </FormControl>
            </FormControl>
        </FormModal>
    );
}
